import { AgGridReact } from "ag-grid-react";
import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { AG_GRID_MODULES, AG_GRID_OPTIONS, VOLUME_FORECAST_TAB_ID, PROMOTION_PLANNING_TAB_ID, SHORT_TERM_PROMOTION_SUB_TAB_ID, PROMO_MASTER_TAB_ID, TOAST_CONTAINER_ID, AG_GRID_DEFAULT_COL_DEF, FORECAST_REVIEW_TAB_ID, ANNUAL_SUMMARY_TAB_ID, TRADE_SPEND_REPORT_TAB_ID } from "../../../../shared/constants";
import EditableGridActions from "../../../UI/EditableGridActions/EditableGridActions";
import { usaNumberFormatter, getToastOptions, removeInsertedRowsById, createDescription } from "../../../../shared/functions";
import { AxiosError, AxiosResponse } from "axios";
import useFetch from "../../../../hooks/useFetchMSAL";
import { useDispatch, useSelector } from "react-redux";
import { PromoMasterSelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/PromoMasterSlicer.model";
import { RootState } from "../../../../store";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import { IRichCellEditorParams } from "ag-grid-community";
import { TradeAndPromotionPlanningFetchAPI } from "../../../../models/APIResponses.model";
import { createPortal } from "react-dom";
import Modal from "../../../UI/Modal/Modal";
import ChangeLogTable from "../../../UI/ChangeLog/ChangeLogTable";

const ShortTermPromotionGrid = (props: {
  gridHeight: string;
  getChangeTrackState: (changeTrackState: boolean) => any;
}) => {
  // Redux variables
  const dispatchFn = useDispatch();
  const globalCustomer: { id: number; name: string; } = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.globalCustomer);
  const tabApiDataFetched: boolean = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.tabApiDataFetched)[SHORT_TERM_PROMOTION_SUB_TAB_ID];
  const activeSubTabId: string = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.activeSubTabId);
  const selectedSlicers: PromoMasterSelectedSlicerModel = useSelector((state: RootState) => state.promoMasterTab.selectedSlicers);
  const dropdownSlicers: { skus: string[]; } = useSelector((state: RootState) => state.promoMasterTab.dropdownSlicers);

  // Variables
  const { getChangeTrackState } = { ...props };
  const agGridRef = useRef<AgGridReact>(null);
  const [, fetchData] = useFetch([]);
  const gridColDef: any = [
    {
      cellDataType: "text",
      headerName: "ID",
      width: 120,
      valueGetter: (params: any) => (params.data.id) ? `PMST-${params.data.id}` : "",
      tooltipValueGetter: (params: any) => params.value
    },
    {
      field: "customer",
      wrapText: true,
      autoHeight: true,
      tooltipField: "customer",
      cellDataType: "text",
      headerName: "Customer"
    },
    {
      field: "sku",
      wrapText: true,
      autoHeight: true,
      tooltipField: "sku",
      cellDataType: "text",
      headerName: "Product",
      editable: (params: any) => {
        if (params.data.id && (params.data.id).toString().startsWith("new_")) {
          return true
        }
        return false
      },
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        valueListMaxWidth: "350px",
        values: dropdownSlicers.skus,
        searchType: "match",
        allowTyping: true,
        filterList: true,
        highlightMatch: true
      } as IRichCellEditorParams,
    },
    {
      field: "Promotion Name",
      wrapText: true,
      autoHeight: true,
      tooltipField: "Promotion Name",
      cellDataType: "text",
      headerName: "Promotion Name",
      editable: true,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      field: "Promo Description",
      wrapText: true,
      autoHeight: true,
      tooltipField: "Promo Description",
      cellDataType: "text",
      headerName: "Promotion Description",
      editable: true,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      field: "Promotion Tactic",
      wrapText: true,
      autoHeight: true,
      tooltipField: "Promotion Tactic",
      cellDataType: "text",
      headerName: "Promotion Tactic",
    },
    {
      colId: "Every Day Price $ / Case",
      field: "Every Day Price $ / Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Long Term Price/Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted
    },
    {
      colId: "Scan Rate/Case",
      field: "Scan Rate/Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Scan Rate/Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "Non Scan Rate/Case",
      field: "Non Scan Rate/Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Non Scan Rate/Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "OI Rate/Case",
      field: "OI Rate/Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "OI Rate / Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "BB Direct Rate/Case",
      field: "BB Direct Rate/Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "BB Direct Rate/Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "total_fixed_per_case",
      field: "total_fixed_per_case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Total Fixed / Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted
    },
    {
      colId: "final_promotional_price",
      field: "final_promotional_price",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Final Promotional Price/Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted
    },
    {
      colId: "Feature Fixed",
      field: "Feature Fixed",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Feature Fixed ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "Coupon Fixed",
      field: "Coupon Fixed",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Coupon Fixed ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "Display Fixed",
      field: "Display Fixed",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Display Fixed ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "total_fixed",
      field: "total_fixed",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Total Fixed ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      valueFormatter: (params: any): any => {
        const numStr: string | null = usaNumberFormatter(params.value, 2);
        return numStr ? `$${numStr}` : numStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted
    },
    {
      colId: "Take Rate",
      field: "Take Rate",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Take Rate (%)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueSetter: (params: any) => {
        const newValue: number | null = parseFloat(params.newValue);
        const columnId = params.column.colId;
        if (isNaN(newValue) || newValue < 0 || newValue > 100) {
          toast.error("Take Rate should be between 0 to 100", {
            ...getToastOptions("error"),
            containerId: TOAST_CONTAINER_ID
          });
          params.data[columnId] = params.oldValue;
        } else {
          params.data[columnId] = usaNumberFormatter(newValue, 2);
        }
      },
      valueFormatter: (params: any): any => {
        const valueStr: string | null | undefined = usaNumberFormatter(params.value, 2);
        return valueStr ? `${valueStr}%` : valueStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "Scan Volume",
      field: "Scan Volume",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Scan Volume",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      valueFormatter: (params: any): any => usaNumberFormatter(params.value, 0),
      tooltipValueGetter: (params: any) => params.valueFormatted
    },
    {
      colId: "Promotion Retail Price$ / Case",
      field: "Promotion Retail Price$ / Case",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Promotion Retail Price/Case ($)",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => {
        const valueStr: string | null | undefined = usaNumberFormatter(params.value, 2);
        return valueStr ? `$${valueStr}` : valueStr;
      },
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    },
    {
      colId: "Incremental Case (Volume) / Week",
      field: "Incremental Case (Volume) / Week",
      minWidth: 100,
      cellDataType: "number",
      headerName: "Incremental Case (Volume) / Week",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      editable: true,
      valueFormatter: (params: any): any => usaNumberFormatter(params.value, 0),
      tooltipValueGetter: (params: any) => params.valueFormatted,
      cellStyle: { backgroundColor: '#ffffcc' }
    }
  ];

  // States
  const [gridRowData, setGridRowData] = useState<any[]>([]);
  const [gridRowDataCopy, setGridRowDataCopy] = useState<any[]>([]);
  const [editedColIds, setEditedColIds] = useState<any[]>([]);
  const [gridCellEdits, setGridCellEdits] = useState<{
    [key: string]: any;
  }>({});
  const [newRowCount, setNewRowCount] = useState<number>(1);
  const [changeTrackState, setChangeTrackState] = useState(false);
  const [insertedRows, setInsertedRows] = useState<any>({});
  const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<any[]>([]);
  const [enableDelButton, setEnableDelButton] = useState(false);
  const [showDuplicateRowButton, setShowDuplicateRowButton] = useState(false);
  const modalCloseBtnRef = useRef(null);
  const [changeLogData, setChangeLogData] = useState([]);
  const [changeLogDataFetched, setChangeLogDataFetched] = useState(false);

  const fetchShortTermPromotionData = useCallback(async () => {
    const fetchingDataToastID = toast.loading("Fetching Short Term Promotion data...", {
      containerId: TOAST_CONTAINER_ID,
      ...getToastOptions("loading"),
    });
    try {
      const fetchAPIResponse: AxiosResponse<any> = await fetchData(`/short-term-promotion`, {
        method: "POST",
        data: {
          customer_id: globalCustomer.id,
          skus: selectedSlicers.skus
        }
      });

      toast.dismiss({
        id: fetchingDataToastID,
        containerId: TOAST_CONTAINER_ID,
      });

      if (fetchAPIResponse.data.data) {
        setGridCellEdits({});
        setInsertedRows({})
        setGridRowData(fetchAPIResponse.data.data);
        setGridRowDataCopy(structuredClone(fetchAPIResponse.data.data));
        dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
          [SHORT_TERM_PROMOTION_SUB_TAB_ID]: true
        }));
        setChangeLogDataFetched(true);

      } else {
        toast.error("Error in fetching Short Term Promotion data", {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("error"),
        });
      }
    } catch (error: Error | AxiosError | any) {
      console.error(`Request Error: ${error.name}`);
      toast.dismiss({
        id: fetchingDataToastID,
        containerId: TOAST_CONTAINER_ID,
      });
      toast.error(error?.response?.data?.error || "Error in fetching Short Term Promotion data", {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("error"),
      });
    }
  }, [dispatchFn, fetchData, selectedSlicers, globalCustomer.id]);


  /**
     * Inserts a new row into the grid
     */
  const addRowToGrid = (): void => {
    const newRowId: string = `new_${newRowCount.toString()}`;
    agGridRef.current!.api!.applyTransaction({
      add: [{ id: newRowId, customer: globalCustomer.name }],
      addIndex: 0,
    });
    setInsertedRows((insertedRows: any) => {
      return {
        ...insertedRows,
        [newRowId]: {
          customer: globalCustomer.name
        }
      };
    });
    setChangeTrackState(true);
    setNewRowCount((prev) => prev + 1);
  };


  /**
     * Updates cell values by using part site source update API
     * @param {any} gridCellEdits Cell edits in the grid
     * @param {any} insertedRows Newly inserted rows
     * @param {"reCalculate" | "update"} mode Re-calculate or update
     */
  const updateCellValues =  async(
    gridCellEdits: any,
    insertedRows: any,
    mode: "reCalculate" | "update",
    data:any={}
  )=> {
    try {
      const processToastID = toast.loading(`${(mode === "update") ? "Updating" : "Re-calculating"} Short Term Promotion data...`, {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("loading"),
      });

      const apiReqResponse: AxiosResponse = await fetchData(
          `/short-term-promotion${(mode === "update") ? "" : "/re-calculate"}`,
          {
            method: (mode === "update") ? "PUT" : "POST",
            data: {
              customer_id: globalCustomer.id,
              skus: selectedSlicers.skus,
              updated_data: gridCellEdits,
              inserted_data: insertedRows,
              description: createDescription(SHORT_TERM_PROMOTION_SUB_TAB_ID, "update", {...gridCellEdits, ...insertedRows}, gridRowData),
              tabname: SHORT_TERM_PROMOTION_SUB_TAB_ID,
              ...data
            }
          }
        );

      toast.dismiss({
        id: processToastID,
        containerId: TOAST_CONTAINER_ID,
      });

      if (apiReqResponse.data.success || apiReqResponse.data.data) {
        toast.success(`Short Term Promotion data ${(mode === "update") ? "updated" : "re-calculated"} successfully`, {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("success"),
        });

        if (mode === "update") {
          dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
            [VOLUME_FORECAST_TAB_ID]: false,
            [PROMOTION_PLANNING_TAB_ID]: false,
            [SHORT_TERM_PROMOTION_SUB_TAB_ID]: false,
            [FORECAST_REVIEW_TAB_ID]: false,
            [ANNUAL_SUMMARY_TAB_ID]: false,
            [TRADE_SPEND_REPORT_TAB_ID]: false
          }));
        } else {
          setGridRowDataCopy(structuredClone(apiReqResponse.data.data));
        }
        setChangeLogDataFetched(true);
        return true;

      } else {
        toast.error(apiReqResponse.data.error, {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("error"),
        });
        return false
      }
    } catch (error: any) {
      console.error(error);
      toast.error(`Error in ${(mode === "update") ? "updating" : "re-calculating"} the Short Term Promotion data`, {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("error"),
      });
      return false;
    }
  };

  const getEveryDayPrice = async (customer: string, sku: string) => {

    const fetchingDataToastID = toast.loading("Fetching EveryDay Price...", {
      containerId: TOAST_CONTAINER_ID,
      ...getToastOptions("loading"),
    });
    try {
      const fetchAPIResponse: AxiosResponse<any> = await fetchData(`/get-everyday-price`, {
        method: "GET",
        params: {
          customer: customer,
          sku: sku
        }
      });

      toast.dismiss({
        id: fetchingDataToastID,
        containerId: TOAST_CONTAINER_ID,
      });
      if (fetchAPIResponse) {
        if (fetchAPIResponse.data.everyday_price_mapping.length > 0) {
          return fetchAPIResponse.data.everyday_price_mapping[0]['Every Day Price $ / Case'];
        } else {
          return 1;
        }

      } else {
        toast.error("Error in fetching EveryDay Prices", {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("error"),
        });
        return {}
      }
    } catch (error: Error | AxiosError | any) {
      console.error(`Request Error: ${error.name}`);
      toast.dismiss({
        id: fetchingDataToastID,
        containerId: TOAST_CONTAINER_ID,
      });
      toast.error(error?.response?.data?.error || "Error in fetching data", {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("error"),
      });
    }
  }

  /**
     * Event handler for when cell editing is stopped
     * @param {any} params Cell editing stop event parameters
     */
  const onCellEditCommit = async (event: any) => {

    try {
      var id: string = event.node.data.id;

      if (id.toString().startsWith("new_")) {
        setInsertedRows((prev: any) => {
          let newData = prev;
          let newRow = newData[id] ? newData[id] : {};
          newRow[event.colDef.field] = event.newValue;
          newData[id] = newRow;
          return newData;
        });
      } else {
        if (
          event.oldValue !== event.newValue &&
          event.newValue !== undefined
        ) {

          event.colDef.cellStyle = (p: any) => {
            return { backgroundColor: '#8af38a' }
          };

          event.api.refreshCells({
            force: true,
            columns: [event.column.getId()],
            rowNodes: [event.node]
          });

          if (editedColIds.indexOf(event.column.getId()) === -1) {
            setEditedColIds((prev) => [...prev, event.column.getId()]);
          }

          //   dispatch(masterTablesTabActions.setIsMasterTablesEdited({ key: 'sourceMaster', value: true }));
          let currentRowNode = agGridRef.current!.api!.getRowNode(
            event.node.id
          );

          setGridCellEdits((prev) => {
            let newTrackEdits = prev;
            let cellEditTrack = newTrackEdits[id] ? newTrackEdits[id] : {};
            cellEditTrack[event.colDef.field] = event.newValue;
            newTrackEdits[id] = cellEditTrack;
            return newTrackEdits;
          });

          setChangeTrackState(true);
          agGridRef.current!.api!.applyTransaction({
            update: [currentRowNode?.data],
          });
        }
      }

      const { colDef, newValue, data } = event;
      const updatedData = { ...data };

      if (updatedData.customer !== undefined
        && updatedData.sku !== undefined) {
        if ((colDef.field === 'customer' || colDef.field === 'sku') && newValue !== event.oldValue) {
          updatedData['Every Day Price $ / Case'] = await getEveryDayPrice(updatedData.customer, updatedData.sku);
        }
      }

      // Promotion Tactic 
      if (['Feature Fixed', 'Coupon Fixed', 'Display Fixed', 'Scan Rate/Case',
        'Non Scan Rate/Case', 'OI Rate/Case', 'BB Direct Rate/Case'].includes(colDef.field) && Object.values(updatedData).some(value => value !== undefined)) {
        let tactic = '';

        if (updatedData['Feature Fixed'] > 0) {
          tactic = (tactic === '') ? "Feature" : `${tactic},Feature`
        }
        if (updatedData['Coupon Fixed'] > 0) {
          tactic = (tactic === '') ? "Coupon" : `${tactic},Coupon`
        }
        if (updatedData['Display Fixed'] > 0) {
          tactic = (tactic === '') ? "Display" : `${tactic},Display`
        }
        if ((updatedData['Scan Rate/Case'] > 0) || (updatedData['Non Scan Rate/Case'] > 0) ||
          (updatedData['BB Direct Rate/Case'] > 0) || (updatedData['OI Rate/Case'] > 0)) {
          tactic = (tactic === '') ? "TPR" : `${tactic},TPR`
        }

        updatedData['Promotion Tactic'] = tactic;
        if (updatedData.id.toString().startsWith("new_")) {
          setInsertedRows((prev: any) => {
            let changes = prev;
            changes[Object.keys(prev)[0]]['Promotion Tactic'] = tactic;
            return changes
          })
        } else {
          setGridCellEdits((prev) => {
            let newTrackEdits = prev;
            let cellEditTrack: any = newTrackEdits[id] ? newTrackEdits[id] : {};
            cellEditTrack['Promotion Tactic'] = tactic;
            newTrackEdits[id] = cellEditTrack;
            return newTrackEdits;
          });
        }
      }

      agGridRef.current?.api.applyTransaction({ update: [updatedData] });
    } catch (error: any) {
      console.error(`Error: ${error}`);
    }

  };

  /**
 * Deletes selected rows
 * @param {any[]} selectedRowIds Selected Row IDs
 */
  const deleteSelectedRowData = async (selectedRowIds: any[], data1:any) => {
    try {
      let deleteToastID;
      // Delete selected newly inserted rows
      const oldDataRowIds: any[] = selectedRowIds.filter(rowId => !rowId.toString().startsWith("new_"));
      setInsertedRows((insertedRows: any) => removeInsertedRowsById(insertedRows, selectedRowIds));

      if (oldDataRowIds.length) {
        deleteToastID = toast.loading("Deleting selected rows...", {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("loading"),
        });

        const deleteReqResponse: AxiosResponse =
          await fetchData(
            `/short-term-promotion`,
            {
              method: 'DELETE',
              data: { 
                row_ids_to_delete: selectedRowIds,
                description: createDescription(SHORT_TERM_PROMOTION_SUB_TAB_ID, "delete", selectedRowData),
                tabname: SHORT_TERM_PROMOTION_SUB_TAB_ID,
                ...data1
               },
            }
          );

        toast.dismiss({
          id: deleteToastID,
          containerId: TOAST_CONTAINER_ID,
        });

        if (deleteReqResponse.data.success) {
          toast.success("Short Term Promotion data updated successfully", {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions("success"),
          });

          dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
            [VOLUME_FORECAST_TAB_ID]: false,
            [PROMOTION_PLANNING_TAB_ID]: false,
            [SHORT_TERM_PROMOTION_SUB_TAB_ID]: false,
            [FORECAST_REVIEW_TAB_ID]: false,
            [ANNUAL_SUMMARY_TAB_ID]: false,
            [TRADE_SPEND_REPORT_TAB_ID]: false
          }));

        } else {
          toast.error(deleteReqResponse.data.error, {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions("error"),
          });
        }
      } else {
        const selectedRows: any[] | undefined = agGridRef.current?.api.getSelectedRows();
        agGridRef.current?.api.applyTransaction({
          remove: selectedRows
        });
      }
    } catch (error: any) {
      console.error(error);
      toast.error("Failed to delete the selected rows", {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("error"),
      });
    }
  };

  /**
* Sets selected rows on selection checkbox change
*/
  const onSelectionChangedHandler = () => {
    const selectedRows = agGridRef.current!.api!.getSelectedRows();
    setSelectedRowData(selectedRows);
    const selectedRowIds: any[] = selectedRows.map((el) => el.id);
    setSelectedRowIds(selectedRowIds);
  };

  /**
 * Deletes selected rows from the grid
 */
  const deleteSelectedRowsFromGrid = (e:MouseEventHandler<HTMLButtonElement>, data:any): void => {
    deleteSelectedRowData(selectedRowIds, data);
    setSelectedRowIds([]);
    setEnableDelButton(false);
  };

  /**
   * Duplicate selected rows
   */
  const duplicateSelectedRowsFromGrid = (): void => {
    if (selectedRowIds.length === 1) {
      selectedRowData.forEach((row) => {
        const newRowId: string = `new_${newRowCount.toString()}`;
        let { id,
          "Promotion Name": promotionName,
          "Promo Description": promotionDescription,
          sum_of_cases,
          total_fixed,
          total_fixed_per_case,
          final_promotional_price,
          expected_percent_lift_over_baseline,
          ...obj } = row;

        agGridRef.current!.api!.applyTransaction({
          add: [{ id: newRowId, ...obj }],
          addIndex: 0,
        });
        // agGridRef.current?.api.applyTransaction({ update: [row] });
        setInsertedRows((insertedRows: any) => {
          let { id,
            "Promotion Name": promotionName,
            "Promo Description": promotionDescription,
            sum_of_cases,
            total_fixed,
            total_fixed_per_case,
            final_promotional_price,
            expected_percent_lift_over_baseline,
            'Every Day Baseline(Volume) / Week': everyday_baseline,
            'Every Day Price $ / Case': everyday_price,
            'Scan Volume': scan_volume,
            ...obj } = row;
          return {
            ...insertedRows,
            [newRowId]: obj
          };
        });
        setChangeTrackState(true);
        setNewRowCount((prev) => prev + 1);
      })
    } else {
      alert('Only single row can be duplicated');
    }

  }


  /** 
     * Removes highlight of edited cells
     */
  const removeEditedCellHighlights = useCallback((): void => {
    editedColIds.forEach((colId) => {
      agGridRef.current!.api.getColumnDef(colId)!.cellStyle = undefined;
    });
  }, [editedColIds]);


  /**
     * Event handler for reset button click
     * @param {any} params Reset button click event data
     */
  const resetCellEdits = async (params: any): Promise<void> => {
    try {
      const resetConsent: boolean = window.confirm(
        "Do you want to reset the changes done to the grid?"
      );

      if (resetConsent) {
        setGridCellEdits({});
        setInsertedRows({});
        setChangeTrackState(false);
        setGridRowDataCopy(structuredClone([...gridRowData]));
        removeEditedCellHighlights();
        setNewRowCount(1);
      }
    } catch (error: Error | any) {
      console.error(`Error: ${error}`);
    }
  };

/**
     * Async function to fetch data from GET /change-log
     */
    const fetchChangeLogData = async (): Promise<void> => {
        const fetchingDataToastID = toast.loading('Fetching change log data...', {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<any>> = await fetchData("/get-change-log", {
                method: "GET",
                params: {
                    tabName: SHORT_TERM_PROMOTION_SUB_TAB_ID
                    }
            });

            if (fetchAPIResponse.data.data) {
                setChangeLogData(fetchAPIResponse.data.data.result)
                setChangeLogDataFetched(false);

            } else {
                toast.error('Error in fetching Change Log data', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });

        } catch (error: Error | any) {
            console.error("Error ", error)
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching Change Log data'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    };

  useEffect(() => {
    if (selectedRowIds.length > 0) {
      setEnableDelButton(true);
      setShowDuplicateRowButton(true);
    } else {
      setEnableDelButton(false);
      setShowDuplicateRowButton(false);
    }
  }, [selectedRowIds]);

  useEffect(() => {
    if (
      Object.keys(insertedRows).length === 0 &&
      Object.keys(gridCellEdits).length === 0
    ) {
      setChangeTrackState(false);
    }
  }, [insertedRows, gridCellEdits]);

  useEffect(() => {
    if (
      !tabApiDataFetched &&
      activeSubTabId === PROMO_MASTER_TAB_ID
    ) {
      fetchShortTermPromotionData();
    }
  }, [tabApiDataFetched, activeSubTabId, fetchShortTermPromotionData]);

  // Update change track state
  useEffect(() => {
    getChangeTrackState(changeTrackState);
  }, [changeTrackState, getChangeTrackState]);

  const rowSelection: any = useMemo(() => {
    return {
      mode: 'multiRow',
      checkboxes: true,
      headerCheckbox: false,
      enableClickSelection: true,
    };
  }, []);

  return (
    <>
      <div className="row mb-2">

        <div className="">
          <div className="d-flex justify-content-end">
            <div className="d-inline">
                <button className="btn btn-warning btn-sm" data-bs-toggle="modal" 
                            data-bs-target={"#changeLogModalShortTermPromotionsId"} onClick={()=>{
                                if(changeLogDataFetched){
                                  fetchChangeLogData();
                                }
                            }}>
                            Change Log
                </button>
            </div>
          <EditableGridActions
            tabId={SHORT_TERM_PROMOTION_SUB_TAB_ID}
            disableIf={{
              resetBtn: !changeTrackState,
              saveBtn: !changeTrackState,
              reCalculateBtn: !changeTrackState,
              deleteRowBtn: !enableDelButton,
              duplicateRowBtn: !showDuplicateRowButton
            }}
            onClick={{
              resetBtn: resetCellEdits,
              saveBtn: (e:MouseEventHandler<HTMLButtonElement>, data:any) =>  {return updateCellValues(gridCellEdits, insertedRows, "update", data)},
              reCalculateBtn: () =>  updateCellValues(gridCellEdits, insertedRows, "reCalculate"),
              addRowBtn: addRowToGrid,
              deleteRowBtn: (e:MouseEventHandler<HTMLButtonElement>, data:any) => deleteSelectedRowsFromGrid(e, data),
              duplicateRowBtn: duplicateSelectedRowsFromGrid
            }}
          />
          </div>

        </div>
      </div>

      <div className="ag-theme-balham mt-2" style={{ height: props.gridHeight, maxHeight: props.gridHeight, width: '100%', overflowX: 'auto', overflowY: 'auto' }}>
        <AgGridReact
          ref={agGridRef}
          rowData={gridRowDataCopy}
          columnDefs={gridColDef}
          rowSelection={rowSelection}
          singleClickEdit={true}
          gridOptions={{
            ...(AG_GRID_OPTIONS as any),
            rowHeight: 22,
            onSelectionChanged: onSelectionChangedHandler,
            suppressMenuHide: true,
            suppressColumnVirtualisation: true,
            autoSizeStrategy: {
              type: "fitCellContents",
              skipHeader: true,
              colIds: [
                "Every Day Price $ / Case", 
                "Scan Rate/Case",
                "Non Scan Rate/Case",
                "OI Rate/Case",
                "BB Direct Rate/Case",
                "total_fixed_per_case",
                "final_promotional_price",
                "Feature Fixed",
                "Coupon Fixed",
                "Display Fixed",
                "total_fixed",
                "Take Rate",
                "Scan Volume",
                "Promotion Retail Price$ / Case",
                "Incremental Case (Volume) / Week"
              ]
            }
          }}
          defaultColDef={{
            ...AG_GRID_DEFAULT_COL_DEF,
          }}
          modules={[
            ...AG_GRID_MODULES
          ]}
          getRowId={(params) => `${params.data.id}`}
          onCellValueChanged={onCellEditCommit}
        />
      </div>

      {
          createPortal(
              <Modal
                  id={"changeLogModalShortTermPromotionsId"}
                  ref={modalCloseBtnRef}
                  title={""}
                  onModalClose={() => { }}
                  width="xl"
              >
                  <ChangeLogTable changeLogData={changeLogData}/>
              </Modal>,
              document.getElementById("modal-root") as HTMLElement
          )
      }
    </>
  );
};

export default ShortTermPromotionGrid;
